/**
 * GCS resumable 업로드 클라이언트.
 *
 * 기존 단일 PUT(`uploadToPresignedUrl`)은 녹화가 끝난 뒤에야 전체 파일을 한 번에 올려,
 * 종료 후 업로드 대기 + 실패 시 전체 재업로드라는 두 비용이 있었다. resumable 세션은:
 *   1) 녹화 중 청크가 나오는 대로 이어올려 종료 후 대기를 최소화하고(스트리밍),
 *   2) 실패해도 서버가 받은 바이트 오프셋부터 이어올린다(재개).
 *
 * 프로토콜(요약):
 *   - init: 백엔드가 발급한 resumable-start 서명 URL 로 POST (`x-goog-resumable: start`,
 *           `x-upload-content-type`) → 응답 `Location` = 세션 URI.
 *   - chunk(마지막 아님): PUT 세션URI, `Content-Range: bytes {s}-{e}/*` → 308 + `Range: bytes=0-{e}`.
 *     ⚠️ 마지막이 아닌 청크는 반드시 256KiB 배수여야 한다.
 *   - chunk(마지막): PUT `Content-Range: bytes {s}-{e}/{total}` → 200/201 = 완료.
 *   - 재개: PUT `Content-Range: bytes *\/{total|*}` (빈 바디) → 308 + `Range` 로 수신 오프셋 확인.
 */

/** GCS resumable 최소 청크 정렬 단위 (256 KiB). 마지막이 아닌 청크는 이 배수여야 한다. */
export const RESUMABLE_CHUNK_ALIGN = 256 * 1024;

export interface ResumableUploadInit {
  /** 백엔드가 발급한 resumable 세션 개시용 POST 서명 URL. */
  sessionInitUrl: string;
  contentType: string;
}

export interface ResumableUploadOptions {
  /** 0~1 진행률 콜백 (전송 확정 바이트 / 전체). 전체가 미정이면 호출되지 않을 수 있음. */
  onProgress?: (uploadedBytes: number, totalBytes: number | undefined) => void;
  signal?: AbortSignal;
  /** 청크 PUT 재시도 횟수 (지수 백오프). 기본 3. */
  maxRetries?: number;
}

class ResumableUploadError extends Error {
  constructor(
    message: string,
    readonly status?: number
  ) {
    super(message);
    this.name = 'ResumableUploadError';
  }
}

/**
 * resumable 세션을 개시하고 세션 URI 를 반환한다.
 * (POST 는 멱등하지 않으므로 세션당 1회만 호출한다.)
 */
export async function startResumableSession(init: ResumableUploadInit): Promise<string> {
  const res = await fetch(init.sessionInitUrl, {
    method: 'POST',
    headers: {
      'x-goog-resumable': 'start',
      'x-upload-content-type': init.contentType,
      // 초기 개시 요청은 바디가 없다.
      'Content-Length': '0',
    },
  });
  if (res.status !== 201) {
    throw new ResumableUploadError(
      `resumable 세션 개시 실패 (${res.status} ${res.statusText})`,
      res.status
    );
  }
  const location = res.headers.get('Location') ?? res.headers.get('location');
  if (!location) {
    throw new ResumableUploadError('resumable 세션 URI(Location)를 응답에서 찾지 못했습니다.');
  }
  return location;
}

/** GCS `Range: bytes=0-{n}` 헤더에서 "다음에 보낼 오프셋"(= n+1)을 계산. 없으면 0. */
function nextOffsetFromRange(rangeHeader: string | null): number {
  if (!rangeHeader) return 0;
  const m = /bytes=0-(\d+)/.exec(rangeHeader);
  if (!m) return 0;
  return Number(m[1]) + 1;
}

/**
 * 세션에 이미 수신된 바이트 오프셋을 조회한다(재개용).
 * @param total 전체 크기(알면 지정, 스트리밍 중이면 undefined)
 */
export async function queryResumableOffset(sessionUri: string, total?: number): Promise<number> {
  const res = await fetch(sessionUri, {
    method: 'PUT',
    headers: { 'Content-Range': `bytes */${total ?? '*'}` },
  });
  if (res.status === 200 || res.status === 201) {
    // 이미 완료됨.
    return total ?? nextOffsetFromRange(res.headers.get('Range'));
  }
  if (res.status === 308) {
    return nextOffsetFromRange(res.headers.get('Range'));
  }
  throw new ResumableUploadError(`오프셋 조회 실패 (${res.status})`, res.status);
}

async function sleep(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}

/**
 * 세션에 하나의 청크를 PUT 한다.
 * @param finalTotal 전체 크기(마지막 청크일 때만 지정, 아니면 undefined → `/*`)
 * @returns 완료 여부(마지막 청크 성공 시 true)
 */
async function putChunk(
  sessionUri: string,
  chunk: Uint8Array,
  offset: number,
  finalTotal: number | undefined,
  maxRetries: number,
  signal?: AbortSignal
): Promise<boolean> {
  const start = offset;
  const end = offset + chunk.byteLength - 1;
  const totalToken = finalTotal !== undefined ? String(finalTotal) : '*';
  const contentRange =
    chunk.byteLength === 0 ? `bytes */${totalToken}` : `bytes ${start}-${end}/${totalToken}`;

  let attempt = 0;
  // 청크 단위 재시도 — 네트워크 오류/5xx 는 오프셋을 재확인해 이어올린다.
  for (;;) {
    if (signal?.aborted) throw new ResumableUploadError('업로드가 취소되었습니다.');
    try {
      const res = await fetch(sessionUri, {
        method: 'PUT',
        headers: { 'Content-Range': contentRange },
        body: chunk.byteLength === 0 ? undefined : (chunk as BodyInit),
        signal,
      });
      if (res.status === 200 || res.status === 201) return true; // 완료
      if (res.status === 308) return false; // 계속
      // 그 외는 재시도 대상(5xx) 또는 실패(4xx)
      if (res.status < 500) {
        throw new ResumableUploadError(`청크 업로드 실패 (${res.status})`, res.status);
      }
      throw new ResumableUploadError(`청크 업로드 일시 오류 (${res.status})`, res.status);
    } catch (err) {
      attempt += 1;
      if (
        attempt > maxRetries ||
        (err instanceof ResumableUploadError && (err.status ?? 500) < 500)
      ) {
        throw err;
      }
      await sleep(Math.min(4000, 500 * 2 ** (attempt - 1)));
    }
  }
}

/**
 * 스트리밍 업로드 세션. 녹화 중 `append(chunk)` 로 청크를 흘려보내고, 종료 시 `complete()` 한다.
 *
 * - 내부 버퍼가 256KiB 배수만큼 쌓이면 정렬해 PUT 한다(마지막 제외).
 * - PUT 은 순차 직렬화된다(GCS 오프셋 정합성).
 * - `complete()` 는 남은 버퍼를 전체 크기와 함께 마지막 청크로 올려 세션을 종료한다.
 */
export class ResumableStreamUpload {
  private pending: Uint8Array[] = [];
  private pendingBytes = 0;
  private offset = 0; // 서버에 확정 전송된 바이트 수
  private queue: Promise<void> = Promise.resolve();
  private completed = false;

  private constructor(
    private readonly sessionUri: string,
    private readonly opts: ResumableUploadOptions
  ) {}

  static async open(
    init: ResumableUploadInit,
    opts: ResumableUploadOptions = {}
  ): Promise<ResumableStreamUpload> {
    const sessionUri = await startResumableSession(init);
    return new ResumableStreamUpload(sessionUri, opts);
  }

  /** 청크를 버퍼에 넣고, 정렬 가능한 만큼 순차 flush 한다. */
  append(chunk: Uint8Array): void {
    if (this.completed) throw new ResumableUploadError('이미 완료된 세션입니다.');
    this.pending.push(chunk);
    this.pendingBytes += chunk.byteLength;
    this.enqueue(() => this.flushAligned());
  }

  /** 남은 버퍼를 마지막 청크로 올리고 세션을 종료한다. 최종 전체 크기를 반환. */
  async complete(): Promise<number> {
    this.enqueue(() => this.flushFinal());
    await this.queue;
    return this.offset;
  }

  private enqueue(task: () => Promise<void>): void {
    this.queue = this.queue.then(task, (e) => {
      // 앞선 실패는 그대로 전파(다음 task 가 잡도록)
      throw e;
    });
  }

  private takeAligned(): Uint8Array | null {
    const aligned = Math.floor(this.pendingBytes / RESUMABLE_CHUNK_ALIGN) * RESUMABLE_CHUNK_ALIGN;
    if (aligned === 0) return null;
    return this.take(aligned);
  }

  private take(n: number): Uint8Array {
    const out = new Uint8Array(n);
    let filled = 0;
    while (filled < n) {
      const head = this.pending[0];
      const need = n - filled;
      if (head.byteLength <= need) {
        out.set(head, filled);
        filled += head.byteLength;
        this.pending.shift();
      } else {
        out.set(head.subarray(0, need), filled);
        this.pending[0] = head.subarray(need);
        filled += need;
      }
    }
    this.pendingBytes -= n;
    return out;
  }

  private async flushAligned(): Promise<void> {
    for (;;) {
      const chunk = this.takeAligned();
      if (!chunk) return;
      await putChunk(
        this.sessionUri,
        chunk,
        this.offset,
        undefined,
        this.opts.maxRetries ?? 3,
        this.opts.signal
      );
      this.offset += chunk.byteLength;
      this.opts.onProgress?.(this.offset, undefined);
    }
  }

  private async flushFinal(): Promise<void> {
    if (this.completed) return;
    const remaining = this.pendingBytes > 0 ? this.take(this.pendingBytes) : new Uint8Array(0);
    const total = this.offset + remaining.byteLength;
    const done = await putChunk(
      this.sessionUri,
      remaining,
      this.offset,
      total,
      this.opts.maxRetries ?? 3,
      this.opts.signal
    );
    if (!done) {
      throw new ResumableUploadError('마지막 청크 이후에도 세션이 완료되지 않았습니다.');
    }
    this.offset = total;
    this.completed = true;
    this.opts.onProgress?.(total, total);
  }
}

/**
 * 완성된 Blob 을 resumable 세션으로 올리는 편의 함수(녹화 종료 후 경로).
 * 단일 PUT 대비 실패 지점 재개 + 진행률을 얻는다.
 */
export async function uploadResumableBlob(
  init: ResumableUploadInit,
  blob: Blob,
  opts: ResumableUploadOptions = {}
): Promise<void> {
  const session = await ResumableStreamUpload.open(init, opts);
  const total = blob.size;
  let sent = 0;
  // 256KiB 정렬 청크로 순차 append (마지막은 complete 가 처리).
  while (sent + RESUMABLE_CHUNK_ALIGN < total) {
    const slice = blob.slice(sent, sent + RESUMABLE_CHUNK_ALIGN);
    session.append(new Uint8Array(await slice.arrayBuffer()));
    sent += RESUMABLE_CHUNK_ALIGN;
  }
  if (sent < total) {
    const tail = blob.slice(sent);
    session.append(new Uint8Array(await tail.arrayBuffer()));
  }
  await session.complete();
}
